import React, { useState, useEffect } from 'react';
import { X, MapPin, Camera } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhotoCard = ({ photo, index, onOpen }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className={`photo-card ${index === 0 ? 'featured' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onOpen(photo)}
      data-testid={`photo-card-${photo.id}`}
    >
      <img src={photo.src} alt={photo.title} loading="lazy" />
      
      {/* Hover Story Overlay */}
      <div className={`photo-story-overlay ${isHovered ? 'active' : ''}`}>
        <div className="photo-story-content">
          <span className="photo-category-tag">{photo.category}</span>
          <h3 className="photo-story-title">{photo.title}</h3>
          <p className="photo-story-text">{photo.description}</p>
          <div className="photo-location">
            <MapPin size={12} />
            <span>{photo.location}</span>
          </div>
        </div>
      </div>
      
      {/* Quick Info (shown when not hovered) */}
      <div className={`photo-quick-info ${isHovered ? 'hidden' : ''}`}>
        <h4>{photo.title}</h4>
        <span>{photo.location}</span>
      </div>
    </div>
  );
};

const PhotoPortfolio = () => {
  const [photos, setPhotos] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(['all']);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await axios.get(`${API}/photos`);
        setPhotos(response.data);
        const uniqueCats = ['all', ...new Set(response.data.map(p => p.category))];
        setCategories(uniqueCats);
      } catch (err) {
        console.error('Error fetching photos:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  const filteredPhotos = filter === 'all' 
    ? photos 
    : photos.filter(img => img.category === filter);

  if (loading) {
    return (
      <div className="section" style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div className="loading-spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  return (
    <div className="photo-portfolio" data-testid="photo-portfolio">
      {/* Gallery Section */}
      <section id="work" className="section">
        <div className="section-header">
          <span className="section-label">Visual Stories</span>
          <h2 className="section-title">Street Photography</h2>
          <p className="subtitle">
            Each frame tells a story—hover to discover the narrative behind the lens
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="filter-buttons" data-testid="filter-buttons">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`filter-btn ${filter === cat ? 'active' : ''}`}
              data-testid={`filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Photo Grid */}
        <div className="photo-grid" data-testid="photo-grid">
          {filteredPhotos.map((photo, index) => (
            <PhotoCard 
              key={photo.id} 
              photo={photo} 
              index={index}
              onOpen={setLightboxPhoto}
            />
          ))}
        </div>
      </section>

      {/* About Photography Section */}
      <div className="section-alt">
        <section id="about" className="section">
          <div className="photo-about-grid">
            <div>
              <span className="section-label">About</span>
              <h2 className="section-title" style={{ marginBottom: '20px' }}>Behind The Lens</h2>
              <p style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '1rem', 
                lineHeight: '1.7',
                marginBottom: '16px'
              }}>
                Photography isn't just a hobby—it's how I see the world differently. As a UX designer, 
                I'm trained to observe human behavior and identify patterns. Through street photography, 
                I apply that same observational eye to capture authentic moments of urban life.
              </p>
              <p style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '1rem', 
                lineHeight: '1.7',
                marginBottom: '24px'
              }}>
                Every frame tells a story of connection—between people and their environment, 
                between light and shadow, between the planned and the spontaneous. Newcastle's 
                historic streets and vibrant culture provide an endless canvas for visual storytelling.
              </p>
              
              <div className="photo-stats" data-testid="photo-stats">
                {/* <div className="photo-stat">
                  <div className="photo-stat-value">5+</div>
                  <div className="photo-stat-label">Years Shooting</div>
                </div>
                <div className="photo-stat">
                  <div className="photo-stat-value">{photos.length}</div>
                  <div className="photo-stat-label">Photos</div>
                </div>
                <div className="photo-stat">
                  <div className="photo-stat-value">UK</div>
                  <div className="photo-stat-label">Based</div>
                </div> */}
              </div>
            </div>

            <div className="photo-feature-image">
              <div className="photo-feature-accent" />
              {photos[0] && (
                <img src={photos[0].src} alt="Featured photography" />
              )}
              <div className="photo-feature-badge">
                <Camera size={28} />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Equipment Section */}
      <section className="section">
        <div className="section-header">
          <span className="section-label">Gear</span>
          <h2 className="section-title">My Setup</h2>
        </div>

        <div className="skills-container" data-testid="equipment-section">
          <div className="skill-card">
            <div className="skill-icon" style={{ background: 'linear-gradient(135deg, #ff9500 0%, #ff6b6b 100%)' }}>
              <Camera size={24} color="white" />
            </div>
            <h3 className="skill-title">Camera</h3>
            <div className="skill-list">
              <span className="skill-tag">Sony A7III</span>
              <span className="skill-tag">Full Frame</span>
              <span className="skill-tag">24MP</span>
              <span className="skill-tag">28-70mm f3.6-5.6</span>
              <span className="skill-tag">20mm f2.8</span>
            </div>
          </div>

          <div className="skill-card">
            <div className="skill-icon" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #5856d6 100%)' }}>
              <Camera size={24} color="white" />
            </div>
            <h3 className="skill-title">Work</h3>
            <div className="skill-list">
              <span className="skill-tag">Product Photography</span>
              <span className="skill-tag">Travel photography</span>
              <span className="skill-tag">Content Creation</span>
            </div>
          </div>

          <div className="skill-card">
            <div className="skill-icon" style={{ background: 'linear-gradient(135deg, #34c759 0%, #30d158 100%)' }}>
              <Camera size={24} color="white" />
            </div>
            <h3 className="skill-title">Post Processing</h3>
            <div className="skill-list">
              <span className="skill-tag">Lightroom Classic</span>
              <span className="skill-tag">Photoshop</span>
              <span className="skill-tag">DaVinci Resolve</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div className="photo-lightbox" onClick={() => setLightboxPhoto(null)} data-testid="photo-lightbox">
          <button className="lightbox-close-btn" data-testid="lightbox-close">
            <X size={20} />
          </button>
          
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <img src={lightboxPhoto.src} alt={lightboxPhoto.title} />
            <div className="lightbox-info">
              <span className="lightbox-category">{lightboxPhoto.category}</span>
              <h3 className="lightbox-title">{lightboxPhoto.title}</h3>
              <p className="lightbox-story">{lightboxPhoto.description}</p>
              <div className="lightbox-location">
                <MapPin size={14} />
                <span>{lightboxPhoto.location}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoPortfolio;
